import { createCustomerPortalSession } from "@/api/privateApi";
import Button from "@/components/Button";
import { NewTag } from "@/components/Tag";
import { SquadBuilderPlayer } from "@/components/molecules/Player/SquadBuilderPlayer/SquadBuilderPlayer";
import { arrow, exchangeIcon } from "@/designSystemIcons";
import { MetaRatingIcon, evolutionsIcon } from "@/icons";
import { cn } from "@/lib/utils";
import {
  Recommendation,
  RecommendationType,
  UpgradeLevel,
  UpgradeType,
} from "@/redux/myClub/myClubSlice";
import { SquadBuilderPlayerType } from "@/redux/squadBuilder/squadBuilderUtils";
import { BREAKPOINTS } from "@/settings";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

const OverallUpgradeArrow = ({ typeColor }: { typeColor?: string }) => (
  <svg
    width="12"
    height="6"
    viewBox="0 0 12 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M0 3.5L6 0.5L12 3.5V5.5L6 2.5L0 5.5V3.5Z" fill={typeColor} />
  </svg>
);

const UpgradeIcon = ({
  upgradeLevel,
  positive,
  typeColor,
}: {
  upgradeLevel?: UpgradeLevel;
  positive?: boolean;
  typeColor?: string;
}) => {
  return upgradeLevel ? (
    <div className="flex flex-col">
      {Array.from({ length: 3 }).map((_, index) => (
        // {Array.from({ length: upgradeLevel }).map((_, index) => (
        <div key={index} className="-mt-0.5">
          <OverallUpgradeArrow typeColor={typeColor} />
        </div>
      ))}
    </div>
  ) : (
    <span
      className={cn(
        "w-2 h-2",
        positive ? "rotate-180 text-ea-dark-green" : "text-ea-red",
      )}
    >
      {arrow}
    </span>
  );
};

const getUpgradeType = ({
  type,
  size = "20",
}: {
  type: UpgradeType;
  size?: string;
}) => {
  switch (type) {
    case "player-meta-upgrade":
      return {
        icon: <SquadMetaRatingIcon width={size} height={size} />,
        title: "Player Meta",
      };
    case "team-meta-upgrade":
      return {
        icon: <MetaRatingIcon width={size} height={size} />,
        title: "Squad Meta",
      };
    case "player-chemistry-upgrade":
      return {
        icon: <PlayerChemistryIcon />,
        title: "Player Chem",
      };
    case "team-chemistry-upgrade":
      return {
        icon: <TeamChemistryIcon />,
        title: "Squad Chem",
      };
    default:
      return {
        icon: <></>,
        title: "",
      };
  }
};

const getTypeColor = ({ type }: { type: RecommendationType }) => {
  switch (type) {
    case "market":
      return "#FDB022";
    case "sbc":
      return "#AB89F5";
    case "objective":
      return "#DA6BD5";
    case "club":
      return "#00C2FF";
    case "evolution":
      return "#1AB25C";
    default:
      return "";
  }
};

const RecommendationActionImg = ({ type }: { type: RecommendationType }) => {
  return (
    <img
      src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/my-club/recommendation-types/${type}.png`}
      className="h-10 left-0 absolute"
    />
  );
};

export const RecommendationCardContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <article className="bg-panel-background rounded-xl transition-all duration-200 min-w-84 mobile:min-w-98 h-68 relative max-mobile:first:ml-2 max-mobile:last:mr-2 max-w-124">
    {children}
  </article>
);

const RecommendationCardHeader = ({
  title,
  isNew,
  type,
  typeColor,
  upgradeLevel,
  isPlaceHolder,
}: {
  title: string;
  isNew?: boolean;
  type?: RecommendationType;
  typeColor?: string;
  upgradeLevel?: UpgradeLevel;
  isPlaceHolder?: boolean;
}) => (
  <section className="h-10 flex flex-row gap-x-4 px-4 py-2 bg-panel-middleground rounded-t-xl relative">
    <div className="flex flex-row gap-x-2 items-center">
      {!isPlaceHolder && <RecommendationActionImg type={type} />}
      <div className="absolute inset-0 flex items-center justify-center gap-x-2">
        {/* <UpgradeIcon typeColor={typeColor} upgradeLevel={upgradeLevel} /> */}
        <h3 className="font-medium text-sm touch-device:text-base">{title}</h3>
        {isNew && <NewTag type="new-design" />}
      </div>
      {/* {expirationDate && (
        <div className="text-xs text-dark-tertiary flex flex-row items-center gap-x-1">
          <span className="w-3 h-3">{expireIcon}</span>
          <p className="mt-0.5">
            {formatTime(expirationDate * 1000 - new Date().getTime())}
          </p>
        </div>
      )} */}
    </div>
  </section>
);

export const EliteSubscriberRecommendationCardHook = ({
  description,
}: {
  description: string;
}) => {
  const navigate = useNavigate();
  return (
    <RecommendationCardContainer>
      <RecommendationCardHeader
        upgradeLevel={3}
        title="Upgrade"
        isPlaceHolder
        typeColor="var(--color-primary-blue)"
      />
      <img
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/my-club/recommendations/empty-recommendation.png`}
        className="w-72 mx-auto absolute flex justify-center items-center inset-0 mt-16"
      />
      <div
        className="absolute top-10 bottom-0 left-0 right-0"
        style={{
          background:
            "radial-gradient(137.38% 126.72% at 50% 0%, #282D34 35.54%, rgba(40, 45, 52, 0.00) 100%)",
        }}
      />

      <p className="text-xs text-center mx-auto w-83 absolute flex justify-center items-center top-22 left-0 right-0 ">
        {description}
      </p>
      <div className="absolute inset-0 flex justify-center items-center mt-12">
        <Button
          variant="primary"
          label="Upgrade Now"
          onClick={() => {
            createCustomerPortalSession();
          }}
        />
      </div>
    </RecommendationCardContainer>
  );
};

export const ErrorRecommendationCard = () => {
  const navigate = useNavigate();
  return (
    <RecommendationCardContainer>
      <div className="flex flex-col items-center h-full gap-y-4 px-14 py-14">
        <p className="flex flex-row gap-x-1 items-center">
          No recommendations available
          <img
            className="w-4 h-4"
            src={
              import.meta.env.VITE_APP_ASSETS_BASE_URL +
              "/misc/error-symbol.png"
            }
          />
        </p>
        <p className="text-sm text-dark-secondary">
          We couldn't get any recommendations for your club. Try importing again
          and make sure you have an active squad.
        </p>
        <Button
          variant="primary"
          label="Import Club"
          onClick={() => navigate("/import")}
        />
      </div>
    </RecommendationCardContainer>
  );
};

export const EmptyRecommendationCard = ({ loading }: { loading?: boolean }) => (
  <RecommendationCardContainer>
    <RecommendationCardHeader
      title="Upgrade"
      isPlaceHolder
      typeColor="silver"
      upgradeLevel={3}
    />
    <img
      src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/my-club/recommendations/empty-recommendation.png`}
      className={cn(
        "w-72 mx-auto absolute flex justify-center items-center inset-0 mt-16",
        loading && "animate-pulse",
      )}
    />
  </RecommendationCardContainer>
);

const Effort = ({ effort }: { effort: string }) => (
  <div className="bg-panel-foreground rounded-t-lg w-full flex flex-row gap-x-1 items-center justify-center text-xs font-medium h-6">
    {effort?.charAt(0).toUpperCase() + effort?.slice(1)} Effort
    <div
      className={cn(
        "w-2 h-2 bg-white rounded-full",
        effort === "low"
          ? "bg-complete"
          : effort === "medium"
            ? "bg-[#FDB022]"
            : "bg-error",
      )}
    />
  </div>
);

export const RecommendationCard = ({
  recommendation: {
    title,
    type,
    individualUpgrades,
    oldPlayer,
    newPlayer,
    isNew,
    effort,
    upgradeLevel,
  },
  onClickPlayer,
}: {
  recommendation?: Recommendation;
  onClickPlayer: ({
    resourceId,
    playerRoleId,
    evoPathId,
    evolutionPath,
    canEvolve,
  }: {
    playerName: string;
    resourceId: number;
    playerRoleId: number;
    evoPathId?: number;
    evolutionPath?: number[];
    canEvolve?: boolean;
  }) => void;
}) => {
  const typeColor = getTypeColor({
    type,
  });
  const isSmallDesktop = useMediaQuery(BREAKPOINTS.SMALL_DESKTOP.query);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  return (
    <RecommendationCardContainer>
      <RecommendationCardHeader
        title={title}
        isNew={isNew}
        type={type}
        typeColor={typeColor}
        upgradeLevel={upgradeLevel}
        isPlaceHolder={false}
      />
      <section className="flex flex-row px-4 py-3">
        <div className="flex flex-col w-24 mobile:w-28">
          <div className="flex flex-col gap-y-0.5 w-25 mobile:w-26">
            {effort && <Effort effort={effort} />}
            {individualUpgrades.map((upgrade, index) => {
              const { icon, title } = getUpgradeType({
                type: upgrade.type,
                size: "16",
              });
              return upgrade.upgradeDelta === 0 ? (
                <React.Fragment key={index} />
              ) : (
                <div
                  className={cn(
                    "flex flex-col bg-panel-middleground px-2 py-1 text-dark-tertiary last:rounded-b-lg w-full",
                    !effort && "first:rounded-t-lg",
                  )}
                  key={index}
                >
                  <p className="text-xs whitespace-nowrap">{title}</p>
                  <div className="flex flex-row items-center justify-between w-ful">
                    {icon}
                    <div className="flex flex-row items-center gap-x-1">
                      <UpgradeIcon positive={upgrade.upgradeDelta > 0} />
                      {upgrade.upgradeDelta !== 0 && (
                        <span className="text-white text-sm font-medium text-right">
                          {upgrade.upgradeDelta}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-row items-center justify-between w-fit mb-2">
          <img
            className={cn(
              "ml-2 w-20 mobile:w-22.5",
              oldPlayer.resourceId &&
                "touch-device:cursor-pointer touch-device:hover:brightness-110 touch-device:hover:scale-[102%] transition-all duration-200",
            )}
            src={
              oldPlayer.resourceId
                ? `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/playercards/small/${oldPlayer?.evoPathId ? oldPlayer?.evoPathId : oldPlayer?.resourceId}.png`
                : import.meta.env.VITE_APP_ASSETS_BASE_URL +
                  "/fc25/my-club/recommendations/empty_card.png"
            }
            onClick={() => {
              if (oldPlayer.resourceId) {
                onClickPlayer({
                  playerName: oldPlayer.cardName,
                  resourceId: oldPlayer.resourceId,
                  playerRoleId: oldPlayer.playerRoleId,
                  evoPathId: oldPlayer.evoPathId,
                  evolutionPath: oldPlayer.evolutionPath,
                  canEvolve: oldPlayer.canEvolve,
                });
              }
            }}
          />
          <div className="text-dark-tertiary w-5 h-5">{exchangeIcon}</div>
          <div
            className="relative touch-device:cursor-pointer touch-device:hover:brightness-110 touch-device:hover:scale-[102%] transition-all duration-200"
            onClick={() =>
              onClickPlayer({
                playerName: newPlayer.cardName,
                resourceId: newPlayer.resourceId,
                playerRoleId: newPlayer.playerRoleId,
                evoPathId: newPlayer.evoPathId,
                evolutionPath: newPlayer.evolutionPath,
                canEvolve: newPlayer.canEvolve,
              })
            }
          >
            <div className="flex items-center justify-center w-[128px] h-[176px]">
              <div className="scale-[140%] mobile:scale-[115%] touch-device:scale-[100%]">
                <SquadBuilderPlayer
                  player={
                    {
                      ...newPlayer,
                      preferredPosition: newPlayer.position,
                      possiblePositions: newPlayer.position,
                      owned: type === "club",
                      isObjectivePlayer: type === "objective",
                      isSbcPlayer: type === "sbc",
                      ...(type === "market" && { price: newPlayer.price }),
                      ...(type === "sbc" && { sbcPrice: newPlayer.sbcPrice }),
                    } as unknown as SquadBuilderPlayerType
                  }
                  playerRoleId={0} // TODO: FIX
                  position={newPlayer.position}
                  metaRating={0}
                  metaRatingZeroChem={0}
                  chemStyleId={0}
                  hideMetaRating={true}
                  tabletOrLargerMode={isSmallDesktop && !isMobile}
                />
              </div>
            </div>
            {type === "evolution" && (
              <div className="flex flex-row gap-x-0 justify-center items-center mx-auto text-xs">
                <div className=" bg-new-gray-850 rounded-l-lg p-1">
                  <div className="flex flex-row items-center gap-x-1">
                    {newPlayer.evolutionCost > 0 ? (
                      <>
                        <span>
                          +{newPlayer.evolutionCost?.toLocaleString()}
                        </span>
                        <img
                          src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/misc/coin.png`}
                          className="w-4 h-4"
                        />
                      </>
                    ) : (
                      <span>Free</span>
                    )}
                  </div>
                </div>
                <div className="rounded-r-lg bg-evolution-active p-1 text-white/80 flex flex-row items-center">
                  {newPlayer.evolutionPath?.length}
                  <div className="w-3 h-3">{evolutionsIcon}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </RecommendationCardContainer>
  );
};

const PlayerChemistryIcon = () => (
  <svg
    width="21"
    height="20"
    viewBox="0 0 21 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.5051 1.37768C12.5051 1.37768 9.46031 0.989746 7.5 0.989746C5.53969 0.989746 2.49491 1.37768 2.49491 1.37768C2.49491 2.44892 1.6265 3.31733 0.555257 3.31733H0V13.7625C0 14.8162 0.610351 15.7746 1.56524 16.2202L7.5 18.9897L8.1434 18.6895L6.92893 17.475C6.53841 17.0845 6.53841 16.4513 6.92893 16.0608L13.2929 9.69685C13.6834 9.30633 14.3166 9.30633 14.7071 9.69685L15 9.98975V3.31733H14.4447C13.3735 3.31733 12.5051 2.44892 12.5051 1.37768ZM15 13.7625V11.404L14 10.404L11.1716 13.2324L13.8901 15.9509C14.5793 15.4464 15 14.6373 15 13.7625ZM12.9646 16.4396L10.4645 13.9395L7.63604 16.7679L9.10764 18.2395L12.9646 16.4396Z"
      fill="currentColor"
    />
    <path
      d="M10.4645 19.5963L13.2929 16.7679L12.9646 16.4396L9.10764 18.2395L10.4645 19.5963Z"
      fill="currentColor"
    />
    <path
      d="M14 16.0608L16.8284 13.2324L15 11.404V13.7625C15 14.6373 14.5793 15.4464 13.8901 15.9509L14 16.0608Z"
      fill="currentColor"
    />
    <path
      d="M17.5355 13.9395L20.364 16.7679L17.5355 19.5963L14.7071 16.7679L17.5355 13.9395Z"
      fill="currentColor"
    />
  </svg>
);

const TeamChemistryIcon = () => (
  <svg
    width="18"
    height="19"
    viewBox="0 0 18 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_2716_51359)">
      <path
        d="M0 12.2397L4.125 8.11475L8.25065 12.2391L4.12582 16.3656L0 12.2397Z"
        fill="currentColor"
      />
      <path
        d="M4.87502 7.36474L9 3.23975L13.125 7.36475L9.00001 11.4897L4.87502 7.36474Z"
        fill="currentColor"
      />
      <path
        d="M9.75 12.2397L13.875 8.11475L18.0002 12.2397L13.875 16.3647L9.75 12.2397Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="clip0_2716_51359">
        <rect
          width="18"
          height="18"
          fill="white"
          transform="translate(0 0.989746)"
        />
      </clipPath>
    </defs>
  </svg>
);

const SquadMetaRatingIcon = ({
  width = "19",
  height = "20",
}: {
  width?: string;
  height?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 19 20"
    fill="none"
  >
    <path
      d="M11.0103 17.5392C10.8581 17.445 10.7121 17.3418 10.5729 17.2304C10.4804 17.1563 10.3909 17.0786 10.3046 16.9974C10.0418 16.7502 9.80934 16.4711 9.61327 16.1663C9.97759 15.6295 10.204 14.9906 10.2722 14.3174L8.21389 15.278L8.2225 15.3071C8.30935 15.5978 8.42032 15.878 8.55309 16.1456C8.56538 16.1703 8.57785 16.195 8.59051 16.2195C9.1133 17.2329 9.95137 18.0572 10.975 18.5627C11.0744 18.6118 11.1756 18.6579 11.2784 18.7008C11.9108 18.9651 12.605 19.1111 13.3333 19.1111C14.0614 19.1111 14.7555 18.9652 15.3878 18.701C15.4934 18.6569 15.5973 18.6095 15.6994 18.5589C15.8779 18.4704 16.0508 18.3721 16.2173 18.2649C16.7891 17.8966 17.286 17.422 17.68 16.869C17.8268 16.6629 17.9593 16.446 18.0761 16.2196C18.0888 16.195 18.1013 16.1704 18.1136 16.1456C18.4676 15.4322 18.6667 14.6282 18.6667 13.7778C18.6667 12.9562 18.4809 12.178 18.1491 11.483C18.1407 11.4653 18.1321 11.4477 18.1235 11.4301C17.6385 10.4423 16.8568 9.62641 15.8947 9.09864C15.8697 9.0849 15.8445 9.07136 15.8193 9.05802C15.077 8.66624 14.231 8.44444 13.3333 8.44444L13.3333 10.9046C14.0474 10.9053 14.7512 10.7146 15.4274 10.35C15.5579 10.2797 15.6873 10.2029 15.8156 10.1197C16.3327 10.4713 16.7714 10.9297 17.0999 11.4632C17.0577 11.5277 17.0174 11.5928 16.9791 11.6585C16.1066 13.1516 16.2139 14.9296 17.0534 16.1664C16.8587 16.4689 16.6281 16.7462 16.3676 16.9922C16.2572 17.0965 16.1414 17.1951 16.0207 17.2877C15.9056 17.3759 15.786 17.4586 15.6624 17.5354C15.4909 17.2874 15.3401 17.0438 15.2083 16.8043L15.1947 16.7795C14.5737 15.6409 14.3833 14.5957 14.4462 13.6118C14.4665 13.2948 14.5131 12.9841 14.58 12.6788C14.2697 12.7488 13.9595 12.7933 13.6482 12.8116C13.4819 12.8213 13.3154 12.8236 13.1483 12.8183C13.0493 12.8152 12.95 12.8093 12.8506 12.8008C12.7012 12.9998 12.5208 13.1762 12.3141 13.3222C12.3232 13.4253 12.3294 13.5291 12.3327 13.6335C12.3701 14.8261 12.02 16.1089 11.0103 17.5392Z"
      fill="currentColor"
    />
    <path
      d="M10.8559 10.1164C11.0408 10.2365 11.2263 10.3424 11.4122 10.4344C12.0524 10.7514 12.6969 10.904 13.3333 10.9046L13.3333 8.44444C12.438 8.44444 11.5942 8.66506 10.8533 9.05491C10.8281 9.06816 10.8031 9.0816 10.7781 9.09524C9.81312 9.62295 9.02912 10.4403 8.54303 11.4304C8.53441 11.4479 8.52588 11.4656 8.51745 11.4832C8.18572 12.1782 8 12.9563 8 13.7778C8 14.2987 8.07467 14.8021 8.21389 15.278L10.2722 14.3174C10.3605 13.4456 10.1836 12.516 9.69727 11.6755C9.65596 11.6041 9.61242 11.5334 9.56662 11.4634C9.89617 10.9281 10.3366 10.4685 10.8559 10.1164Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.66667 0C8.40917 0 11.1156 0.344828 11.1156 0.344828C11.1156 1.29704 11.8876 2.06897 12.8398 2.06897H13.3333L13.3333 7.55556C9.89689 7.55556 7.11111 10.3413 7.11111 13.7778C7.11111 14.433 7.21239 15.0646 7.40015 15.6577L6.66667 16L1.39133 13.5382C0.542534 13.1421 0 12.2902 0 11.3535V2.06897H0.493561C1.44578 2.06897 2.2177 1.29704 2.2177 0.344828C2.2177 0.344828 4.92417 0 6.66667 0Z"
      fill="currentColor"
    />
    <path
      d="M12.3141 13.3222C12.5208 13.1762 12.7012 12.9998 12.8506 12.8008C12.6442 12.7832 12.4369 12.7539 12.2282 12.7129C12.267 12.9136 12.2961 13.1166 12.3141 13.3222Z"
      fill="currentColor"
    />
  </svg>
);
